import axios from "utils/axios";

type AuthUserType = {
  name: string | undefined;
  username: string | undefined;
  email: string | undefined;
  userType: string | undefined;
  company?: any | undefined;
  country: any;
  phone: string | undefined;
  password: string | undefined;
};

type UserDataType = {
  email: string;
  password: string;
};

/**
 * get company list
 * @returns Array of company details with id and name
 */
export const getCompanyList = () =>
  new Promise((resolve, reject) => {
    axios
      .get("/company/getcompany/list")
      .then((response) => resolve(response.data))
      .catch((error) => reject(error));
  });

/**
 * create a new user
 * @param data : user data
 * @returns Promise resolved with user account details
 */
export const createAuthUser = (data: AuthUserType) =>
  new Promise((resolve, reject) => {
    axios
      .post("/users/create", data)
      .then((response) => resolve(response.data))
      .catch((error) => reject(error));
  });

export const userLogin = ({ email, password }: UserDataType) =>
  new Promise((resolve, reject) => {
    axios
      .post(`/users/login`, {
        email,
        password,
      })
      .then((response) => {
        // Handle the standardized response format from middleware
        const standardizedResponse = response.data;

        console.log('🔵 [userLogin API] Raw standardizedResponse:', standardizedResponse);

        // V2 Detection: Backend serializer returns {user: {...}, token: "..."}
        // The middleware wraps it in {success: true, data: {user, token, ...}}
        if (standardizedResponse.success && standardizedResponse.data) {
          const innerData = standardizedResponse.data;

          // V2 Format: innerData = {user: {...}, token: "..."}
          if (innerData.user) {
            console.log('🔵 [userLogin API] Detected V2 format');
            const onboarding =
              innerData.onboarding ||
              innerData.user?.onboarding ||
              standardizedResponse.onboarding ||
              null;

            resolve({
              user: innerData.user,          // V2: Frontend expects this
              token: innerData.token,
              requiresOnboarding: innerData.requiresOnboarding,
              // Also include legacy keys for backward compat
              response: innerData.user,
              message: standardizedResponse.message || 'Access Granted',
              id: innerData.user?._id,
              candidateId: innerData.candidateId || innerData.user?.candidateId,
              ...(onboarding ? { onboarding } : {}),
            });
            return;
          }
        }

        // Legacy/Fallback handling (candidate login, older flows)
        let userData, token, id, message, flow, candidateId;

        if (standardizedResponse.success && standardizedResponse.data) {
          userData = standardizedResponse.data;
          token = standardizedResponse.token;
          id = standardizedResponse.id || standardizedResponse.data._id;
          message = standardizedResponse.message;
          flow = standardizedResponse.flow || standardizedResponse.data.flow;
          candidateId = standardizedResponse.candidateId || standardizedResponse.data.candidateId;
        } else if (standardizedResponse.response) {
          userData = standardizedResponse.response;
          token = standardizedResponse.token;
          id = standardizedResponse.id;
          message = standardizedResponse.message;
          flow = standardizedResponse.flow;
          candidateId = standardizedResponse.candidateId;
        } else {
          userData = standardizedResponse;
          token = standardizedResponse.token;
          id = standardizedResponse.id || standardizedResponse._id;
          candidateId = standardizedResponse.candidateId;
        }

        const onboarding =
          standardizedResponse.onboarding ||
          userData?.onboarding ||
          null;

        const transformedResponse: any = {
          message: message || 'Access Granted',
          token: token,
          id: id,
          flow: flow,
          candidateId: candidateId,
          response: userData,
        };

        if (onboarding) {
          transformedResponse.onboarding = onboarding;
        }

        console.log('🔵 [userLogin API] Legacy format, transformed:', transformedResponse);
        resolve(transformedResponse);
      })
      .catch((error) => reject(error));
  });

// verify token (legacy — mutates global axios defaults, avoid in new code)
export const verifyToken = (token: string) => {
  if (token === null) {
    return Promise.reject(new Error("Token is null"));
  } else {
    axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
    return axios
      .post("/users/verify-token")
      .then((response) => response.data)
      .catch((error) => Promise.reject(error));
  }
};

/**
 * Bootstrap-only token verification.
 * Sends Authorization header explicitly per-request — no reliance on
 * Redux state, axios interceptors, or global mutable defaults.
 */
export const verifyTokenDirect = (token: string) => {
  if (!token) {
    return Promise.reject(new Error("Token is null or empty"));
  }
  return axios
    .post("/users/verify-token", {}, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((response) => response.data);
};

export const verifyCandidateAccount = (userId: string, code: string) =>
  new Promise((resolve, reject) => {
    axios
      .post(`/users/candidate/verify/account/${userId}/${code}`)
      .then((response) => resolve(response.data))
      .catch((error) => reject(error));
  });

export const updateWelcomeCompleted = (userId: string, welcomeCompleted: boolean) =>
  new Promise((resolve, reject) => {
    axios
      .patch(`/users/update/onboarding/welcome/${userId}`, { welcomeCompleted })
      .then((response) => resolve(response.data))
      .catch((error) => reject(error));
  });

export const markOnboardingPageVisited = (
  userId: string,
  pageType: 'candidate' | 'company',
  pageKey: string,
) =>
  new Promise((resolve, reject) => {
    axios
      .patch(`/users/update/onboarding/page/${userId}`, { pageType, pageKey })
      .then((response) => resolve(response.data))
      .catch((error) => reject(error));
  });


// REMOVED: getPlan - legacy job limit API deleted
// Use getDepartmentJobStatus from api/dashboard/department instead

